import AsyncStorage from '@react-native-async-storage/async-storage';
import { Linking, Platform } from 'react-native';

import Plentiful from '../../constants/Plentiful';
import store from '../../store/Store';
import { logError } from '../../utils/logger';
import { ReviewService } from './ReviewService';

/**
 * Native implementation of ReviewService for iOS and Android platforms.
 * Opens the appropriate app store page for leaving a review:
 * - iOS: the write-review deep link on the App Store
 * - Android: the app listing on Google Play (no write-review deep link available)
 *
 * Note: The store URL is read from Plentiful.STORE_REVIEW_URLS keyed by Platform.OS.
 */
export default class NativeReviewService implements ReviewService {
  private static readonly STORAGE_KEY_PREFIX = '@review_prompt_last_shown_';

  private static readonly FREQUENCY_LIMIT_DAYS = 183; // Approximately 6 months

  private stateStore;

  private asyncStore;

  constructor() {
    this.asyncStore = AsyncStorage;
    this.stateStore = store;
  }

  async requestReview(): Promise<void> {
    try {
      const canRequest = await this.canRequestReview();
      if (!canRequest) {
        return;
      }

      const url = Plentiful.STORE_REVIEW_URLS[Platform.OS as 'ios' | 'android'];
      if (url) {
        await Linking.openURL(url);
        await this.updateLastShown();
      } else if (__DEV__) {
        // eslint-disable-next-line no-console
        console.log(`No store review URL configured for platform: ${Platform.OS}`);
      }
    } catch (error) {
      logError('Review prompt failed', {
        error: error instanceof Error ? error.message : String(error),
        userId: this.getUserId(),
      });
      // Fail silently - don't disrupt user experience
    }
  }

  async canRequestReview(): Promise<boolean> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        return false; // No user logged in
      }

      // Check frequency limit
      const lastShown = await this.getLastShown(userId);
      if (lastShown) {
        const daysSinceLastShown = (Date.now() - lastShown) / (1000 * 60 * 60 * 24);
        if (daysSinceLastShown < NativeReviewService.FREQUENCY_LIMIT_DAYS) {
          return false;
        }
      }

      return true;
    } catch (error) {
      logError('Review eligibility check failed', {
        error: error instanceof Error ? error.message : String(error),
        userId: this.getUserId(),
      });
      return false; // Conservative fallback
    }
  }

  async resetForTesting(): Promise<void> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        return;
      }

      const storageKey = NativeReviewService.getStorageKey(userId);
      await this.asyncStore.removeItem(storageKey);

      logError('Review prompt reset by internal user', {
        userId,
        action: 'reset_for_testing',
      });
    } catch (error) {
      logError('Review prompt reset failed', {
        error: error instanceof Error ? error.message : String(error),
        userId: this.getUserId(),
      });
    }
  }

  private async getLastShown(userId: number): Promise<number | null> {
    try {
      const storageKey = NativeReviewService.getStorageKey(userId);
      const lastShownStr = await this.asyncStore.getItem(storageKey);
      return lastShownStr ? parseInt(lastShownStr, 10) : null;
    } catch (error) {
      logError('Failed to get last review prompt timestamp', {
        error: error instanceof Error ? error.message : String(error),
        userId,
      });
      return null;
    }
  }

  private async updateLastShown(): Promise<void> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        return;
      }

      const storageKey = NativeReviewService.getStorageKey(userId);
      await this.asyncStore.setItem(storageKey, Date.now().toString());
    } catch (error) {
      logError('Failed to update review prompt timestamp', {
        error: error instanceof Error ? error.message : String(error),
        userId: this.getUserId(),
      });
    }
  }

  private static getStorageKey(userId: number): string {
    return `${NativeReviewService.STORAGE_KEY_PREFIX}${userId}`;
  }

  /**
   * Gets the current user ID from Redux store.
   * Returns null if no user is logged in.
   */
  private getUserId(): number | null {
    try {
      const state = this.stateStore.getState() as any; // Use any to handle dynamic state structure
      return state?.registration?.user?.id || null;
    } catch (error) {
      logError('Failed to get user ID from Redux store', {
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }
}
